'use client';

import React from 'react';
import { Helmet } from 'react-helmet-async';
import { usePathname } from 'next/navigation';
import { SITE_URL, ORGANIZATION_SAME_AS } from '../../data/organization';
import { ADULT_RATING_VALUE, RTA_LABEL, pathRequiresRtaLabel } from '../../utils/adultContentRating';

interface SEOProps {
  title: string;
  description: string;
  canonicalUrl?: string;
  ogImage?: string;
  ogImageAlt?: string;
  article?: boolean;
  breadcrumbs?: Array<{name: string, url: string}>;
  trailingSlash?: boolean;
  // Controls whether to output SiteNavigationElement JSON-LD (helps Google show pill-style sitelinks).
  includeNavigationSchema?: boolean;
  // Article-specific Open Graph tags
  publishedTime?: string; // ISO 8601 format
  modifiedTime?: string; // ISO 8601 format
  author?: string;
  authorUrl?: string;
}

const SEO: React.FC<SEOProps> = ({
  title,
  description,
  canonicalUrl,
  ogImage = '/heavengirlfriend_social_sharing.webp',
  ogImageAlt = 'Heaven Girlfriend - Digital Romance, Evolved',
  article = false,
  breadcrumbs,
  trailingSlash = false,
  includeNavigationSchema = true,
  publishedTime,
  modifiedTime,
  author,
  authorUrl,
}) => {
  const pathname = usePathname();
  const siteUrl = SITE_URL;
  const defaultTitle = 'Heaven Girlfriend | Expert AI Reviews';
  const defaultDescription = 'Expert reviews of the best AI girlfriend platforms. Compare features, pricing, and find your perfect AI girlfriend match.';
  
  // Add trailing slash if specified (for review pages)
  let pathWithSlash = pathname;
  if (trailingSlash && !pathname.endsWith('/')) {
    pathWithSlash = `${pathname}/`;
  }
  const currentUrl = canonicalUrl || `${siteUrl}${pathWithSlash}`;

  // Ensure title and description exist and don't exceed recommended lengths
  const providedTitle = typeof title === 'string' && title.trim().length > 0 ? title.trim() : defaultTitle;
  const providedDescription = typeof description === 'string' && description.trim().length > 0 ? description.trim() : defaultDescription;
  const MAX_TITLE_LENGTH = 60;
  const MAX_DESCRIPTION_LENGTH = 155;

  const truncateWithEllipsis = (value: string, maxLength: number) => {
    if (value.length <= maxLength) {
      return value;
    }

    const sliceLength = Math.max(0, maxLength - 3);
    return `${value.substring(0, sliceLength).trim()}...`;
  };

  const safeTitle = truncateWithEllipsis(providedTitle, MAX_TITLE_LENGTH);
  const safeDescription = truncateWithEllipsis(providedDescription, MAX_DESCRIPTION_LENGTH);

  // Create Navigation JSON-LD
  const navigationSchema = {
    '@context': 'https://schema.org',
    '@type': 'SiteNavigationElement',
    'itemListElement': [
      {
        '@type': 'ListItem',
        'position': 1,
        'name': 'Home',
        'item': siteUrl
      },
      {
        '@type': 'ListItem',
        'position': 2,
        'name': 'Best',
        'item': `${siteUrl}/best`
      },
      {
        '@type': 'ListItem',
        'position': 3,
        'name': 'Reviews',
        'item': `${siteUrl}/reviews`
      },
      {
        '@type': 'ListItem',
        'position': 4,
        'name': 'News',
        'item': `${siteUrl}/news`
      },
      {
        '@type': 'ListItem',
        'position': 5,
        'name': 'Blog',
        'item': `${siteUrl}/blog`
      },
      {
        '@type': 'ListItem',
        'position': 6,
        'name': 'About Us',
        'item': `${siteUrl}/about-us`
      },
      {
        '@type': 'ListItem',
        'position': 7,
        'name': 'Contact Us',
        'item': `${siteUrl}/contact-us`
      }
    ]
  };

  // Create Website Schema
  const websiteSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: 'Heaven Girlfriend',
    url: siteUrl,
    description: defaultDescription,
    keywords: 'AI girlfriend reviews, best AI girlfriend platforms, AI girlfriend, AI girlfriends, virtual girlfriend, AI reviews',
    potentialAction: {
      '@type': 'SearchAction',
      target: `${siteUrl}/search-results?q={search_term_string}`,
      'query-input': 'required name=search_term_string'
    }
  };

  // Create Organization Schema
  const organizationSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'Heaven Girlfriend',
    url: siteUrl,
    logo: `${siteUrl}/heavengirlfriend_logo.webp`,
    description: 'Heaven Girlfriend provides expert reviews and comparisons of AI girlfriend platforms to help users find the best option.',
    sameAs: [...ORGANIZATION_SAME_AS]
  };

  // Create generic Product schema without self-referential ratings or reviews.
  const productSchema = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: 'AI Girlfriend Platform Reviews',
    description: 'Comprehensive reviews and comparisons of AI girlfriend platforms',
    image: `${siteUrl}/heavengirlfriend_social_sharing.webp`,
    offers: {
      '@type': 'Offer',
      price: '0',
      priceCurrency: 'USD',
      availability: 'https://schema.org/InStock'
    }
  };

  // Create Breadcrumb Schema
  const defaultBreadcrumbItems = [
    {
      '@type': 'ListItem',
      position: 1,
      name: 'Heaven Girlfriend',
      item: siteUrl
    }
  ];

  // If custom breadcrumbs are provided, add them
  if (breadcrumbs && breadcrumbs.length > 0) {
    breadcrumbs.forEach((crumb, index) => {
      defaultBreadcrumbItems.push({
        '@type': 'ListItem',
        position: index + 2,
        name: crumb.name,
        item: crumb.url.startsWith('http') ? crumb.url : `${siteUrl}${crumb.url}`
      });
    });
  } else if (pathname !== '/') {
    // Add current page as breadcrumb
    const pageName = pathname.split('/').filter(Boolean).pop() || '';
    const formattedPageName = pageName
      .split('-')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
    
    defaultBreadcrumbItems.push({
      '@type': 'ListItem',
      position: 2,
      name: formattedPageName || title,
      item: currentUrl
    });
  }

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: defaultBreadcrumbItems
  };

  // Create ImageObject schema for the OG image
  const imageObjectSchema = {
    '@context': 'https://schema.org',
    '@type': 'ImageObject',
    contentUrl: `${siteUrl}${ogImage}`,
    name: ogImageAlt,
    description: safeDescription,
    uploadDate: '2026-06-01T08:00:00+08:00'
  };
  
  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{safeTitle}</title>
      <meta name="description" content={safeDescription} />
      <meta name="keywords" content="Heaven Girlfriend, AI girlfriend, AI girlfriends, best AI girlfriend, AI girlfriend platforms, digital romance, virtual girlfriend, emotional support, AI chat, digital relationship, AI companion, online relationship" />
      {pathRequiresRtaLabel(pathname) && (
        <>
          <meta name="rating" content={ADULT_RATING_VALUE} />
          <meta name="rating" content={RTA_LABEL} />
        </>
      )}
      
      {/* Canonical URL */}
      <link rel="canonical" href={currentUrl} />
      
      {/* Favicon */}
      <link rel="icon" type="image/x-icon" href="/heavengirlfriend_favicon.ico" />
      <link rel="icon" type="image/png" sizes="32x32" href="/heavengirlfriend_favicon-32x32.png" />
      <link rel="icon" type="image/png" sizes="16x16" href="/heavengirlfriend_favicon-16x16.png" />
      <link rel="apple-touch-icon" sizes="180x180" href="/heavengirlfriend_apple-touch-icon.png" />
      <link rel="manifest" href="/manifest.json" />
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content={article ? 'article' : 'website'} />
      <meta property="og:url" content={currentUrl} />
      <meta property="og:title" content={safeTitle} />
      <meta property="og:description" content={safeDescription} />
      <meta property="og:image" content={`${siteUrl}${ogImage}`} />
      <meta property="og:image:alt" content={ogImageAlt} />
      <meta property="og:site_name" content="Heaven Girlfriend" />
      <meta property="og:locale" content="en_US" />
      
      {/* Article-specific Open Graph tags */}
      {article && publishedTime && (
        <meta property="article:published_time" content={publishedTime} />
      )}
      {article && modifiedTime && (
        <meta property="article:modified_time" content={modifiedTime} />
      )}
      {article && author && (
        <meta property="article:author" content={author} />
      )}
      {article && authorUrl && (
        <meta property="article:author" content={authorUrl} />
      )}
      
      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content="@heavengirlfriend" />
      <meta name="twitter:title" content={safeTitle} />
      <meta name="twitter:description" content={safeDescription} />
      <meta name="twitter:image" content={`${siteUrl}${ogImage}`} />
      <meta name="twitter:image:alt" content={ogImageAlt} />

      {/* Additional SEO Meta Tags */}
      <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
      <meta name="googlebot" content="index, follow" />
      <meta name="format-detection" content="telephone=no" />
      <meta name="theme-color" content="#EC4899" />
      
      {/* Application Information */}
      <meta name="application-name" content="Heaven Girlfriend" />
      <meta name="apple-mobile-web-app-title" content="Heaven Girlfriend" />
      <meta name="category" content="Lifestyle" />
      <meta name="price" content="Free" />
      
      {/* Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify(websiteSchema)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(organizationSchema)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(productSchema)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(breadcrumbSchema)}
      </script>
      {includeNavigationSchema && (
        <script type="application/ld+json">
          {JSON.stringify(navigationSchema)}
        </script>
      )}
      <script type="application/ld+json">
        {JSON.stringify(imageObjectSchema)}
      </script>
    </Helmet>
  );
};

export default SEO;
